import createEngine, {
  DagreEngine,
  DiagramModel,
} from "@projectstorm/react-diagrams";
import * as React from "react";
import AdvancedBuilder from "./AdvancedBuilder";
import {
  Action,
  DeleteItemsAction,
  InputType,
  ZoomCanvasAction,
} from "@projectstorm/react-canvas-core";
import LogicNodeFactory from "./nodes/Logic/LogicNodeFactory";
import CustomLinkFactory from "./links/CustomLinkFactory";
import CustomPortFactory from "./ports/CustomPort/CustomPortFactory";

const MAX_ZOOM_LEVEL = 80;
const CENTERING_ANIMATION_DURATION = 200;

export default function ProjectBuilder() {
  // 1) setup the diagram engine
  const engine = createEngine({ registerDefaultDeleteItemsAction: false });
  const actionEventBus = engine.getActionEventBus();
  let centeringAnimationFrame;

  const centerNodeHorizontally = (nodeCenterX, canvasCenterX) => {
    cancelAnimationFrame(centeringAnimationFrame);

    const startOffsetX = engine.model.getOffsetX();
    const targetOffsetX = startOffsetX + canvasCenterX - nodeCenterX;
    const startTime = performance.now();
    const animate = (timestamp) => {
      const progress = Math.min(
        1,
        (timestamp - startTime) / CENTERING_ANIMATION_DURATION
      );
      const easedProgress = 1 - (1 - progress) ** 3;

      engine.model.setOffset(
        startOffsetX + (targetOffsetX - startOffsetX) * easedProgress,
        engine.model.getOffsetY()
      );
      engine.repaintCanvas();

      if (progress < 1) {
        centeringAnimationFrame = requestAnimationFrame(animate);
      }
    };

    centeringAnimationFrame = requestAnimationFrame(animate);
  };

  actionEventBus
    .getActionsForType(InputType.MOUSE_WHEEL)
    .filter((action) => action instanceof ZoomCanvasAction)
    .forEach((action) => actionEventBus.deregisterAction(action));

  actionEventBus.registerAction(
    new Action({
      type: InputType.MOUSE_WHEEL,
      fire: ({ event }) => {
        const model = engine.getModel();
        const node = event.target.closest(".custom-node");
        const canvas = event.currentTarget.closest(".diagram-container");
        const nodeBounds = node?.getBoundingClientRect();
        const canvasBounds = canvas?.getBoundingClientRect();
        const canPanNode =
          nodeBounds &&
          canvasBounds &&
          ((event.deltaY > 0 && nodeBounds.bottom > canvasBounds.bottom) ||
            (event.deltaY < 0 && nodeBounds.top < canvasBounds.top));

        if (model.getZoomLevel() >= MAX_ZOOM_LEVEL && canPanNode) {
          const remainingOverflow =
            event.deltaY > 0
              ? nodeBounds.bottom - canvasBounds.bottom
              : nodeBounds.top - canvasBounds.top;
          const panDelta =
            event.deltaY > 0
              ? Math.min(event.deltaY, remainingOverflow)
              : Math.max(event.deltaY, remainingOverflow);

          const nodeCenterX = nodeBounds.left + nodeBounds.width / 2;
          const canvasCenterX = canvasBounds.left + canvasBounds.width / 2;

          event.stopPropagation();
          model.setOffset(model.getOffsetX(), model.getOffsetY() - panDelta);
          centerNodeHorizontally(nodeCenterX, canvasCenterX);
          engine.repaintCanvas();
          return;
        }

        const oldZoomFactor = model.getZoomLevel() / 100;
        let scrollDelta = event.deltaY;
        scrollDelta = event.ctrlKey && scrollDelta % 1 !== 0
          ? scrollDelta / 3
          : scrollDelta / 60;

        const zoomLevel = Math.min(
          MAX_ZOOM_LEVEL,
          Math.max(10, model.getZoomLevel() + scrollDelta)
        );
        model.setZoomLevel(zoomLevel);

        const zoomFactor = zoomLevel / 100;
        const boundingRect = event.currentTarget.getBoundingClientRect();
        const widthDiff = boundingRect.width * (zoomFactor - oldZoomFactor);
        const heightDiff = boundingRect.height * (zoomFactor - oldZoomFactor);
        const xFactor =
          (event.clientX - boundingRect.left - model.getOffsetX()) /
          oldZoomFactor /
          boundingRect.width;
        const yFactor =
          (event.clientY - boundingRect.top - model.getOffsetY()) /
          oldZoomFactor /
          boundingRect.height;

        event.stopPropagation();
        model.setOffset(
          model.getOffsetX() - widthDiff * xFactor,
          model.getOffsetY() - heightDiff * yFactor
        );
        engine.repaintCanvas();
      },
    })
  );

  // disable dangling links
  engine
    .getStateMachine()
    .getCurrentState().dragNewLink.config.allowLooseLinks = false;

  // use shift-delete to remove nodes instead of delete
  engine.getActionEventBus().registerAction(
    new DeleteItemsAction({
      keyCodes: [46],
      modifiers: {
        shiftKey: true,
      },
    })
  );

  // register all the custom factories
  engine.getNodeFactories().registerFactory(new LogicNodeFactory());
  engine.getLinkFactories().registerFactory(new CustomLinkFactory());
  engine.getPortFactories().registerFactory(new CustomPortFactory());

  const dagreEngine = new DagreEngine({
    graph: {
      rankdir: "LR",
      // ranker: "longest-path",
      marginx: 40,
      marginy: 40,
      ranksep: 200,
    },
    includeLinks: true,
  });

  // 2) setup the diagram model
  const model = new DiagramModel();

  // 5) load model into engine
  engine.setModel(model);

  // 6) render the diagram!

  return <AdvancedBuilder engine={engine} dagreEngine={dagreEngine} />;
}
